import { useState, useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useUrlFilters } from '../../hooks/useUrlFilters'
import { presenceApi } from '../../api/endpoints'
import Badge from '../../components/ui/Badge'
import EmptyState from '../../components/ui/EmptyState'
import ScrollableTabs from '../../components/ui/ScrollableTabs'
import {
  Monitor,
  Smartphone,
  Clock,
  Users,
  Activity,
  Wifi,
  WifiOff,
  Coffee,
  Loader2,
  Phone,
} from 'lucide-react'

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function fmtDuration(seconds: number): string {
  if (!seconds || seconds < 0) return '0m'
  const h = Math.floor(seconds / 3600)
  const m = Math.floor((seconds % 3600) / 60)
  if (h === 0) return `${m}m`
  return `${h}h ${m}m`
}

function fmtTime(isoString: string | null | undefined): string {
  if (!isoString) return '--'
  try {
    const d = new Date(isoString)
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return '--'
  }
}

function fmtPercent(value: number): string {
  return `${Math.round(value)}%`
}

function dateParamRange(days: string): { date_from: string; date_to: string } {
  const end = new Date()
  const start = new Date()
  start.setDate(end.getDate() - parseInt(days, 10))
  return {
    date_from: start.toISOString().slice(0, 10),
    date_to: end.toISOString().slice(0, 10),
  }
}

const PERIOD_OPTIONS = [
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
]

const DAY_LABELS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']

const TABS = ['Timeline', 'Daily Report', 'Weekly Trend', 'Heatmap', 'Correlation'] as const
type TabName = (typeof TABS)[number]

// ---------------------------------------------------------------------------
// KPI Card
// ---------------------------------------------------------------------------

function KpiCard({
  label,
  value,
  icon: Icon,
  color,
}: {
  label: string
  value: string | number
  icon: React.ComponentType<{ size?: number; className?: string }>
  color: 'green' | 'yellow' | 'gray' | 'blue' | 'indigo'
}) {
  const colorMap = {
    green: 'bg-green-50 text-green-600',
    yellow: 'bg-yellow-50 text-yellow-600',
    gray: 'bg-gray-100 text-gray-500',
    blue: 'bg-blue-50 text-blue-600',
    indigo: 'bg-indigo-50 text-indigo-600',
  }
  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-3">
      <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorMap[color]}`}>
        <Icon size={20} />
      </div>
      <div>
        <p className="text-2xl font-semibold text-gray-900">{value}</p>
        <p className="text-xs text-gray-500">{label}</p>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Status dot
// ---------------------------------------------------------------------------

function StatusDot({ status }: { status: string }) {
  const map: Record<string, { bg: string; label: string }> = {
    active: { bg: 'bg-green-500', label: 'Active' },
    idle: { bg: 'bg-yellow-400', label: 'Idle' },
    offline: { bg: 'bg-gray-300', label: 'Offline' },
  }
  const s = map[status] || map.offline
  return (
    <span className="inline-flex items-center gap-1.5 text-sm">
      <span className={`w-2 h-2 rounded-full ${s.bg}`} />
      {s.label}
    </span>
  )
}

// ---------------------------------------------------------------------------
// Device icon
// ---------------------------------------------------------------------------

function DeviceIcon({ device }: { device: string }) {
  if (device === 'mobile') return <Smartphone size={15} className="text-gray-400" />
  return <Monitor size={15} className="text-gray-400" />
}

// ---------------------------------------------------------------------------
// Timeline bar (div-based hourly chart)
// ---------------------------------------------------------------------------

function TimelineChart({ hourly }: { hourly: Record<string, number> }) {
  // Parse keys like "09:web:active" into { hour, state }
  const hours: { hour: number; active: number; idle: number }[] = Array.from({ length: 24 }, (_, i) => ({
    hour: i,
    active: 0,
    idle: 0,
  }))

  for (const [key, seconds] of Object.entries(hourly)) {
    const parts = key.split(':')
    const h = parseInt(parts[0], 10)
    const state = parts[2] || 'active'
    if (h >= 0 && h < 24) {
      if (state === 'active') hours[h].active += seconds
      else if (state === 'idle') hours[h].idle += seconds
    }
  }

  const maxSeconds = Math.max(3600, ...hours.map((h) => h.active + h.idle))

  return (
    <div>
      <div className="flex items-end gap-px sm:gap-0.5 h-[140px] sm:h-[120px]">
        {hours.map((h) => {
          const activeH = (h.active / maxSeconds) * 100
          const idleH = (h.idle / maxSeconds) * 100
          return (
            <div
              key={h.hour}
              className="flex-1 flex flex-col justify-end rounded-t-sm overflow-hidden group relative"
              style={{ height: '100%' }}
              tabIndex={0}
            >
              <div className="bg-green-500 rounded-t-sm" style={{ height: `${activeH}%` }} />
              <div className="bg-yellow-300" style={{ height: `${idleH}%` }} />
              {/* Tooltip — shows on hover (desktop) and focus (mobile tap) */}
              <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none whitespace-nowrap z-10">
                {h.hour}:00 - Active: {fmtDuration(h.active)}, Idle: {fmtDuration(h.idle)}
              </div>
            </div>
          )
        })}
      </div>
      <div className="flex gap-px sm:gap-0.5 mt-1">
        {hours.map((h) => (
          <div key={h.hour} className="flex-1 text-center text-[8px] sm:text-[9px] text-gray-400">
            {h.hour % 4 === 0 ? h.hour : ''}
          </div>
        ))}
      </div>
      <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-2 bg-green-500 rounded-sm" /> Active
        </span>
        <span className="inline-flex items-center gap-1">
          <span className="w-3 h-2 bg-yellow-300 rounded-sm" /> Idle
        </span>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Heatmap Grid
// ---------------------------------------------------------------------------

function HeatmapGrid({ cells }: { cells: Array<{ day_of_week: number; hour: number; active_seconds: number; idle_seconds: number; total_seconds: number; idle_ratio: number }> }) {
  // Build a lookup: [day][hour]
  const grid: Record<string, { total_seconds: number; idle_ratio: number }> = {}
  for (const c of cells) {
    grid[`${c.day_of_week}-${c.hour}`] = { total_seconds: c.total_seconds, idle_ratio: c.idle_ratio }
  }

  function cellColor(idle_ratio: number, total: number): string {
    if (total === 0) return 'bg-gray-50'
    if (idle_ratio <= 20) return 'bg-green-400'
    if (idle_ratio <= 50) return 'bg-yellow-300'
    return 'bg-red-400'
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[700px]">
        {/* Hour labels */}
        <div className="flex gap-0.5 ml-10 mb-1">
          {Array.from({ length: 24 }, (_, h) => (
            <div key={h} className="flex-1 text-center text-[9px] text-gray-400">
              {h % 3 === 0 ? `${h}` : ''}
            </div>
          ))}
        </div>
        {/* Rows */}
        {Array.from({ length: 7 }, (_, d) => (
          <div key={d} className="flex items-center gap-0.5 mb-0.5">
            <div className="w-10 text-xs text-gray-500 text-right pr-2">{DAY_LABELS[d]}</div>
            {Array.from({ length: 24 }, (_, h) => {
              const cell = grid[`${d}-${h}`]
              const total = cell?.total_seconds || 0
              const ratio = cell?.idle_ratio || 0
              return (
                <div
                  key={h}
                  className={`flex-1 h-5 rounded-sm ${cellColor(ratio, total)} group relative cursor-default`}
                  tabIndex={0}
                >
                  <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-1 px-2 py-1 bg-gray-900 text-white text-[10px] rounded shadow-lg opacity-0 group-hover:opacity-100 group-focus-within:opacity-100 pointer-events-none whitespace-nowrap z-10">
                    {DAY_LABELS[d]} {h}:00 - {fmtDuration(total)} ({fmtPercent(ratio)} idle)
                  </div>
                </div>
              )
            })}
          </div>
        ))}
        {/* Legend */}
        <div className="flex items-center gap-3 mt-3 ml-10 text-xs text-gray-500">
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-gray-50 rounded-sm border border-gray-200" /> No data</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded-sm" /> Mostly active</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-yellow-300 rounded-sm" /> Mixed</span>
          <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-red-400 rounded-sm" /> Mostly idle</span>
        </div>
      </div>
    </div>
  )
}

// ---------------------------------------------------------------------------
// Main Page
// ---------------------------------------------------------------------------

export default function PresenceDashboardPage() {
  const [filters, setFilter] = useUrlFilters({ period: '7' })
  const [selectedUserId, setSelectedUserId] = useState<number | null>(null)
  const [activeTab, setActiveTab] = useState<TabName>('Timeline')

  const dateRange = useMemo(() => dateParamRange(filters.period), [filters.period])

  // ---- Queries ----

  const liveQuery = useQuery({
    queryKey: ['presence', 'live'],
    queryFn: async () => {
      const res = await presenceApi.getLive()
      return res.data
    },
    refetchInterval: 30_000, // refresh every 30s
  })

  const agentTodayQuery = useQuery({
    queryKey: ['presence', 'agent-today', selectedUserId],
    queryFn: async () => {
      if (!selectedUserId) return null
      const res = await presenceApi.getAgentToday(selectedUserId)
      return res.data as { total_seconds: number; hourly: Record<string, number> }
    },
    enabled: !!selectedUserId && activeTab === 'Timeline',
  })

  const dailyQuery = useQuery({
    queryKey: ['presence', 'daily', dateRange],
    queryFn: async () => {
      const res = await presenceApi.getDailyReport(dateRange)
      return res.data
    },
    enabled: activeTab === 'Daily Report',
  })

  const weeklyQuery = useQuery({
    queryKey: ['presence', 'weekly', dateRange],
    queryFn: async () => {
      const res = await presenceApi.getWeeklyReport(dateRange)
      return res.data
    },
    enabled: activeTab === 'Weekly Trend',
  })

  const heatmapQuery = useQuery({
    queryKey: ['presence', 'heatmap', dateRange],
    queryFn: async () => {
      const res = await presenceApi.getHeatmap(dateRange)
      return res.data
    },
    enabled: activeTab === 'Heatmap',
  })

  const correlationQuery = useQuery({
    queryKey: ['presence', 'correlation', dateRange],
    queryFn: async () => {
      const res = await presenceApi.getCorrelation(dateRange)
      return res.data
    },
    enabled: activeTab === 'Correlation',
  })

  // ---- Derived KPIs ----

  const onlineUsers = liveQuery.data?.online || []
  const activeCount = onlineUsers.filter((u) => u.status === 'active').length
  const idleCount = onlineUsers.filter((u) => u.status === 'idle').length
  // Estimate total from daily summaries or fall back to online count
  const estimatedTotal = Math.max(onlineUsers.length + 5, activeCount + idleCount + 3) // conservative estimate
  const offlineCount = Math.max(0, estimatedTotal - activeCount - idleCount)
  const coverage = estimatedTotal > 0 ? ((activeCount + idleCount) / estimatedTotal) * 100 : 0

  // ---- Selected user info ----
  const selectedUser = onlineUsers.find((u) => u.user_id === selectedUserId)

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Team Activity</h1>
          <p className="text-sm text-gray-500 mt-0.5">Monitor who is online and how your team spends their time</p>
        </div>
        <select
          value={filters.period}
          onChange={(e) => setFilter('period', e.target.value)}
          className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 bg-white focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
        >
          {PERIOD_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        <KpiCard label="Online Now" value={activeCount} icon={Wifi} color="green" />
        <KpiCard label="Idle Now" value={idleCount} icon={Coffee} color="yellow" />
        <KpiCard label="Offline" value={offlineCount} icon={WifiOff} color="gray" />
        <KpiCard label="On a Call" value={0} icon={Phone} color="blue" />
        <KpiCard label="Team Coverage" value={fmtPercent(coverage)} icon={Users} color="indigo" />
      </div>

      {/* Agent Status Table */}
      <div className="bg-white rounded-xl border border-gray-200">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h2 className="text-sm font-semibold text-gray-800">Who is online</h2>
          {liveQuery.isRefetching && <Loader2 size={14} className="text-gray-400 animate-spin" />}
        </div>

        {liveQuery.isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 size={24} className="text-indigo-500 animate-spin" />
          </div>
        ) : onlineUsers.length === 0 ? (
          <EmptyState icon={Users} title="Nobody online" description="No team members are currently active." />
        ) : (
          <>
            {/* Mobile card view */}
            <div className="md:hidden divide-y divide-gray-100">
              {onlineUsers.map((u) => (
                <div
                  key={u.user_id}
                  onClick={() => {
                    setSelectedUserId(u.user_id)
                    setActiveTab('Timeline')
                  }}
                  className={`p-4 cursor-pointer transition-colors ${
                    selectedUserId === u.user_id ? 'bg-indigo-50' : 'active:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div>
                      <p className="text-sm font-medium text-gray-900">{u.full_name || u.username}</p>
                      <p className="text-xs text-gray-400">{u.role}</p>
                    </div>
                    <StatusDot status={u.status} />
                  </div>
                  <div className="flex items-center gap-4 text-xs text-gray-500">
                    <span className="inline-flex items-center gap-1">
                      <DeviceIcon device={u.device_type} />
                      <span className="capitalize">{u.device_type}</span>
                    </span>
                    {u.last_action && <span>{u.last_action}</span>}
                    <span>Seen {fmtTime(u.last_seen_at)}</span>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop table view */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                    <th className="px-5 py-2 font-medium">Name</th>
                    <th className="px-5 py-2 font-medium">Status</th>
                    <th className="px-5 py-2 font-medium">Device</th>
                    <th className="px-5 py-2 font-medium">Last Action</th>
                    <th className="px-5 py-2 font-medium">Last Seen</th>
                  </tr>
                </thead>
                <tbody>
                  {onlineUsers.map((u) => (
                    <tr
                      key={u.user_id}
                      onClick={() => {
                        setSelectedUserId(u.user_id)
                        setActiveTab('Timeline')
                      }}
                      className={`border-b border-gray-50 cursor-pointer transition-colors ${
                        selectedUserId === u.user_id
                          ? 'bg-indigo-50'
                          : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-5 py-2.5">
                        <div>
                          <p className="font-medium text-gray-900">{u.full_name || u.username}</p>
                          <p className="text-xs text-gray-400">{u.role}</p>
                        </div>
                      </td>
                      <td className="px-5 py-2.5">
                        <StatusDot status={u.status} />
                      </td>
                      <td className="px-5 py-2.5">
                        <span className="inline-flex items-center gap-1.5">
                          <DeviceIcon device={u.device_type} />
                          <span className="text-gray-600 capitalize">{u.device_type}</span>
                        </span>
                      </td>
                      <td className="px-5 py-2.5 text-gray-600">
                        {u.last_action || '--'}
                      </td>
                      <td className="px-5 py-2.5 text-gray-500">
                        {fmtTime(u.last_seen_at)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}
      </div>

      {/* Tabs Section */}
      <div className="bg-white rounded-xl border border-gray-200">
        {/* Tab headers */}
        <ScrollableTabs
          tabs={TABS.map(tab => ({ key: tab, label: tab }))}
          activeKey={activeTab}
          onChange={(key) => setActiveTab(key as TabName)}
          className="px-5"
        />

        {/* Tab content */}
        <div className="p-5">
          {/* Timeline */}
          {activeTab === 'Timeline' && (
            <div>
              {!selectedUserId ? (
                <div className="text-center py-8 text-sm text-gray-400">
                  <Clock size={32} className="mx-auto mb-2 text-gray-300" />
                  Select a team member from the table above to see their activity timeline
                </div>
              ) : agentTodayQuery.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="text-indigo-500 animate-spin" />
                </div>
              ) : agentTodayQuery.data ? (
                <div>
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="text-sm font-medium text-gray-800">
                      {selectedUser?.full_name || selectedUser?.username || 'Agent'} &mdash; Today
                    </h3>
                    <Badge variant="info">{fmtDuration(agentTodayQuery.data.total_seconds)} total</Badge>
                  </div>
                  <TimelineChart hourly={agentTodayQuery.data.hourly || {}} />
                </div>
              ) : (
                <div className="text-center py-8 text-sm text-gray-400">
                  No activity data for today
                </div>
              )}
            </div>
          )}

          {/* Daily Report */}
          {activeTab === 'Daily Report' && (
            <div>
              {dailyQuery.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="text-indigo-500 animate-spin" />
                </div>
              ) : !dailyQuery.data?.summaries?.length ? (
                <EmptyState icon={Activity} title="No daily data" description="No presence data found for this date range." />
              ) : (
                <>
                {/* Mobile card view */}
                <div className="md:hidden space-y-3">
                  {(dailyQuery.data.summaries as any[]).map((s: any, i: number) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-900">{s.full_name || s.username}</p>
                        <span className="text-xs text-gray-400">{s.summary_date}</span>
                      </div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-lg font-semibold text-gray-900">{fmtDuration(s.total_online_seconds)}</span>
                        <span className="text-xs text-green-600">{fmtDuration(s.active_seconds)} active</span>
                        <span className="text-xs text-yellow-600">{fmtDuration(s.idle_seconds)} idle</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{s.session_count} sessions</span>
                        <span>In: {fmtTime(s.first_login_at)}</span>
                        <span>Out: {fmtTime(s.last_logout_at)}</span>
                        {s.device_type && (
                          <span className="inline-flex items-center gap-1">
                            <DeviceIcon device={s.device_type} />
                            <span className="capitalize">{s.device_type}</span>
                          </span>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table view */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium">Name</th>
                        <th className="px-4 py-2 font-medium">Date</th>
                        <th className="px-4 py-2 font-medium">Online Time</th>
                        <th className="px-4 py-2 font-medium">Active</th>
                        <th className="px-4 py-2 font-medium">Idle</th>
                        <th className="px-4 py-2 font-medium">Sessions</th>
                        <th className="px-4 py-2 font-medium">First Login</th>
                        <th className="px-4 py-2 font-medium">Last Logout</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(dailyQuery.data.summaries as any[]).map((s: any, i: number) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">{s.full_name || s.username}</td>
                          <td className="px-4 py-2 text-gray-600">{s.summary_date}</td>
                          <td className="px-4 py-2 text-gray-900">{fmtDuration(s.total_online_seconds)}</td>
                          <td className="px-4 py-2 text-green-600">{fmtDuration(s.active_seconds)}</td>
                          <td className="px-4 py-2 text-yellow-600">{fmtDuration(s.idle_seconds)}</td>
                          <td className="px-4 py-2 text-gray-600">{s.session_count}</td>
                          <td className="px-4 py-2 text-gray-500">{fmtTime(s.first_login_at)}</td>
                          <td className="px-4 py-2 text-gray-500">{fmtTime(s.last_logout_at)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
              )}
            </div>
          )}

          {/* Weekly Trend */}
          {activeTab === 'Weekly Trend' && (
            <div>
              {weeklyQuery.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="text-indigo-500 animate-spin" />
                </div>
              ) : !weeklyQuery.data?.weeks?.length ? (
                <EmptyState icon={Activity} title="No weekly data" description="No presence data found for this date range." />
              ) : (
              <>
                {/* Mobile card view */}
                <div className="md:hidden space-y-3">
                  {(weeklyQuery.data.weeks as any[]).map((w: any, i: number) => (
                    <div key={i} className="bg-white rounded-xl border border-gray-200 p-4">
                      <div className="flex items-center justify-between mb-2">
                        <p className="text-sm font-medium text-gray-900">{w.full_name || w.username}</p>
                        <span className="text-xs text-gray-400">{w.week_start} - {w.week_end}</span>
                      </div>
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-lg font-semibold text-gray-900">{fmtDuration(w.total_online_seconds)}</span>
                        <span className="text-xs text-green-600">{fmtDuration(w.active_seconds)} active</span>
                        <span className="text-xs text-yellow-600">{fmtDuration(w.idle_seconds)} idle</span>
                      </div>
                      <div className="flex items-center gap-4 text-xs text-gray-500">
                        <span>{w.session_count} sessions</span>
                        <span>{w.days_active} days active</span>
                        <span>Avg: {fmtDuration(w.avg_daily_seconds)}/day</span>
                      </div>
                    </div>
                  ))}
                </div>

                {/* Desktop table view */}
                <div className="hidden md:block overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium">Name</th>
                        <th className="px-4 py-2 font-medium">Week</th>
                        <th className="px-4 py-2 font-medium">Online Time</th>
                        <th className="px-4 py-2 font-medium">Active</th>
                        <th className="px-4 py-2 font-medium">Idle</th>
                        <th className="px-4 py-2 font-medium">Sessions</th>
                        <th className="px-4 py-2 font-medium">Days Active</th>
                        <th className="px-4 py-2 font-medium">Avg Daily</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(weeklyQuery.data.weeks as any[]).map((w: any, i: number) => (
                        <tr key={i} className="border-b border-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">{w.full_name || w.username}</td>
                          <td className="px-4 py-2 text-gray-600">{w.week_start} - {w.week_end}</td>
                          <td className="px-4 py-2 text-gray-900">{fmtDuration(w.total_online_seconds)}</td>
                          <td className="px-4 py-2 text-green-600">{fmtDuration(w.active_seconds)}</td>
                          <td className="px-4 py-2 text-yellow-600">{fmtDuration(w.idle_seconds)}</td>
                          <td className="px-4 py-2 text-gray-600">{w.session_count}</td>
                          <td className="px-4 py-2 text-gray-600">{w.days_active}</td>
                          <td className="px-4 py-2 text-gray-600">{fmtDuration(w.avg_daily_seconds)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
              )}
            </div>
          )}

          {/* Heatmap */}
          {activeTab === 'Heatmap' && (
            <div>
              {heatmapQuery.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="text-indigo-500 animate-spin" />
                </div>
              ) : !heatmapQuery.data?.cells?.length ? (
                <EmptyState icon={Activity} title="No heatmap data" description="No presence data found for this date range." />
              ) : (
                <div>
                  <h3 className="text-sm font-medium text-gray-800 mb-3">Activity heatmap by day and hour</h3>
                  {/* Mobile: simplified day-by-day list */}
                  <div className="sm:hidden space-y-2">
                    {DAY_LABELS.map((dayLabel, d) => {
                      const dayCells = (heatmapQuery.data.cells as any[]).filter((c: any) => c.day_of_week === d)
                      const totalActive = dayCells.reduce((sum: number, c: any) => sum + (c.active_seconds || 0), 0)
                      const totalIdle = dayCells.reduce((sum: number, c: any) => sum + (c.idle_seconds || 0), 0)
                      const totalSeconds = totalActive + totalIdle
                      const idlePct = totalSeconds > 0 ? Math.round((totalIdle / totalSeconds) * 100) : 0
                      const peakHour = dayCells.reduce((best: any, c: any) => (!best || c.total_seconds > best.total_seconds) ? c : best, null)
                      return (
                        <div key={d} className="bg-white rounded-xl border border-gray-200 p-3 flex items-center justify-between">
                          <div>
                            <p className="text-sm font-medium text-gray-900">{dayLabel}</p>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {totalSeconds > 0 ? (
                                <>
                                  <span className="text-green-600">{fmtDuration(totalActive)} active</span>
                                  {' / '}
                                  <span className="text-yellow-600">{fmtDuration(totalIdle)} idle</span>
                                </>
                              ) : (
                                'No data'
                              )}
                            </p>
                          </div>
                          <div className="text-right">
                            {totalSeconds > 0 && (
                              <>
                                <p className="text-sm font-semibold text-gray-900">{fmtDuration(totalSeconds)}</p>
                                <p className="text-[10px] text-gray-400">
                                  {idlePct}% idle{peakHour ? ` / peak ${peakHour.hour}:00` : ''}
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      )
                    })}
                    <div className="flex items-center gap-3 mt-2 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-green-400 rounded-sm" /> Active</span>
                      <span className="inline-flex items-center gap-1"><span className="w-3 h-3 bg-yellow-300 rounded-sm" /> Idle</span>
                    </div>
                  </div>
                  {/* Desktop: full heatmap grid */}
                  <div className="hidden sm:block">
                    <HeatmapGrid cells={heatmapQuery.data.cells} />
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Correlation */}
          {activeTab === 'Correlation' && (
            <div>
              {correlationQuery.isLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 size={20} className="text-indigo-500 animate-spin" />
                </div>
              ) : !correlationQuery.data?.users?.length ? (
                <EmptyState icon={Activity} title="No correlation data" description="No data available for this date range." />
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-xs text-gray-500 border-b border-gray-100">
                        <th className="px-4 py-2 font-medium">Name</th>
                        <th className="px-4 py-2 font-medium">Online Hours</th>
                        <th className="px-4 py-2 font-medium">Active Hours</th>
                        <th className="px-4 py-2 font-medium">Calls</th>
                        <th className="px-4 py-2 font-medium">Call Duration</th>
                        <th className="px-4 py-2 font-medium">Calls / Online Hour</th>
                      </tr>
                    </thead>
                    <tbody>
                      {correlationQuery.data.users.map((u) => (
                        <tr key={u.user_id} className="border-b border-gray-50">
                          <td className="px-4 py-2 font-medium text-gray-900">{(u as any).full_name || u.username}</td>
                          <td className="px-4 py-2 text-gray-600">{u.online_hours.toFixed(1)}h</td>
                          <td className="px-4 py-2 text-green-600">{u.active_hours.toFixed(1)}h</td>
                          <td className="px-4 py-2 text-gray-900">{u.call_count}</td>
                          <td className="px-4 py-2 text-gray-600">{u.call_duration_minutes.toFixed(0)}m</td>
                          <td className="px-4 py-2">
                            <Badge variant={u.calls_per_online_hour >= 1 ? 'success' : 'warning'}>
                              {u.calls_per_online_hour.toFixed(1)}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
